// Holds the reviews widget's estimated height until Yotpo draws, so content below does not jump when it does.
class YotpoReviewsReserve extends HTMLElement {
	// data-yotpo-element-loaded is not usable as the signal: Yotpo sets it while the widget is still 0px tall.
	// Not .yotpo-main-layout: Yotpo inserts that as an empty shell before its review data arrives (25px tall on HH). With
	// a slow API the shell held still past the settle time, so the reserve released and the rows then pushed everything
	// down again. The header, filters and rows all arrive with .yotpo-reviews-container, in one frame.
	static #DRAWN_SELECTOR = '.yotpo-reviews-container, .yotpo-empty-state';
	static #WIDGET_SELECTOR = '.yotpo-widget-instance';
	static #CONTENT_SELECTOR = ':scope > .yotpo-reviews-reserve__content';
	static #SKELETON_SELECTOR = ':scope > .yotpo-reviews-reserve__skeleton';
	// Yotpo inserts its layout a frame before it has any height, then fills it over a few more frames. Releasing on the
	// first drawn frame collapsed the whole reservation for that frame (a 1.0 layout shift on GM), so the release waits
	// for the drawn height to hold still.
	static #SETTLE_MS = 500;
	// An ad blocker or a Yotpo outage means it never draws; without a cutoff the page keeps a blank block of up to 4,900px.
	static #RELEASE_TIMEOUT_MS = 10000;
	static #MS_PER_SECOND = 1000;

	#resizeObserver = null;
	#settleId = null;
	#timeoutId = null;
	#startTimeout = () => {
		this.#timeoutId = setTimeout(() => {
			this.#warnUndrawn();
			this.#release();
		}, YotpoReviewsReserve.#RELEASE_TIMEOUT_MS);
	};

	connectedCallback() {
		if (!this.dataset.reserve) {
			return;
		}
		// GM's redesign templates collapse the full widget behind the reviews hero's button (display: none until
		// clicked), so it never takes space on load and nothing should be held for it.
		if (this.#isWidgetHidden()) {
			this.#release();
			return;
		}
		// The initial observation also covers a widget Yotpo drew before this deferred script upgraded the element.
		this.#resizeObserver = new ResizeObserver(([entry]) => this.#onContentResize(entry.contentRect.height));
		this.#resizeObserver.observe(this.querySelector(YotpoReviewsReserve.#CONTENT_SELECTOR));
		if (document.readyState === 'complete') {
			this.#startTimeout();
		} else {
			window.addEventListener('load', this.#startTimeout, { once: true });
		}
	}

	disconnectedCallback() {
		this.#stop();
	}

	#onContentResize(height) {
		clearTimeout(this.#settleId);
		if (height === 0 || !this.#isDrawn()) {
			return;
		}
		if (height >= parseFloat(getComputedStyle(this).minHeight)) {
			this.#release();
			return;
		}
		this.#settleId = setTimeout(() => this.#release(), YotpoReviewsReserve.#SETTLE_MS);
	}

	#isWidgetHidden() {
		return this.querySelector(YotpoReviewsReserve.#WIDGET_SELECTOR)?.getClientRects().length === 0;
	}

	#isDrawn() {
		return this.querySelector(YotpoReviewsReserve.#DRAWN_SELECTOR) !== null;
	}

	// Content with height but no drawn signal is either Yotpo's empty shell still waiting on its review data, or a Yotpo
	// update that renamed the classes the release waits for. The second would hold every reviewed page for 10s, so the
	// warning names both rather than only "did not render".
	#warnUndrawn() {
		if (this.#isDrawn()) {
			return;
		}
		const seconds = YotpoReviewsReserve.#RELEASE_TIMEOUT_MS / YotpoReviewsReserve.#MS_PER_SECOND;
		const contentHeight = this.querySelector(YotpoReviewsReserve.#CONTENT_SELECTOR).getBoundingClientRect().height;
		if (contentHeight === 0) {
			console.warn(`Yotpo reviews did not render within ${seconds}s of page load; releasing the reserved space.`);
			return;
		}
		console.warn(
			`Yotpo reviews started drawing, but none of "${YotpoReviewsReserve.#DRAWN_SELECTOR}" appeared within ${seconds}s ` +
				`of page load: its review data is still loading, or Yotpo renamed those classes. Releasing the reserved space.`
		);
	}

	#release() {
		this.#stop();
		delete this.dataset.reserve;
		// CSS already hides it; removing it drops about 230 nodes that will never show again.
		this.querySelector(YotpoReviewsReserve.#SKELETON_SELECTOR)?.remove();
	}

	#stop() {
		this.#resizeObserver?.disconnect();
		this.#resizeObserver = null;
		clearTimeout(this.#settleId);
		clearTimeout(this.#timeoutId);
		window.removeEventListener('load', this.#startTimeout);
	}
}

if (!customElements.get('yotpo-reviews-reserve')) {
	customElements.define('yotpo-reviews-reserve', YotpoReviewsReserve);
}
